import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  AccountRole,
  AccountUser,
  deleteUser as deleteUserApi,
  fetchUsers,
  inviteUser as inviteUserApi,
  updateUserAnalysisGroups as updateUserAnalysisGroupsApi
} from "../../../data/queries/users";
import { logError } from "../../../utils/error";
import { useAuth } from "../../auth/AuthContext";

// Query keys factory for better cache management
const userKeys = {
  all: ["users"] as const,
  lists: () => [...userKeys.all, "list"] as const,
  list: (tenantId: string) => [...userKeys.lists(), { tenantId }] as const
};

type UseUsersResponse = {
  users: AccountUser[];
  isLoading: boolean;
  error: Error | null;
  inviteUser: {
    mutate: (variables: { email: string; role: AccountRole }) => void;
    isPending: boolean;
    error: Error | null;
    isSuccess: boolean;
  };
  deleteUser: {
    mutate: (userId: string) => void;
    isPending: boolean;
    error: Error | null;
    isSuccess: boolean;
  };
  updateUserAnalysisGroups: {
    mutate: (variables: { userId: string; analysisGroups: string[] | null }) => void;
    isPending: boolean;
    error: Error | null;
    isSuccess: boolean;
  };
  refreshUsers: () => void;
};

export const useUsers = (): UseUsersResponse => {
  const { tenantId } = useAuth();
  const queryClient = useQueryClient();

  // Query for users list
  const usersQuery = useQuery({
    queryKey: userKeys.list(tenantId || ""),
    queryFn: () => fetchUsers(tenantId!),
    enabled: !!tenantId,
    staleTime: 2 * 60 * 1000 // 2 minutes
  });

  // Mutation for inviting users
  const inviteUserMutation = useMutation({
    mutationFn: ({ email, role }: { email: string; role: AccountRole }) =>
      inviteUserApi(email, role, tenantId!),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: userKeys.list(tenantId!) });
    },
    onError: (error) => {
      logError("Error inviting user", error);
    }
  });

  // Mutation for deleting users
  const deleteUserMutation = useMutation({
    mutationFn: (userId: string) => deleteUserApi(userId, tenantId!),
    onSuccess: () => {
      // Invalidate and refetch users list
      queryClient.invalidateQueries({ queryKey: userKeys.list(tenantId!) });
    },
    onError: (error) => {
      logError("Error deleting user", error);
    }
  });

  // Mutation for updating user analysis groups with optimistic updates
  const updateUserAnalysisGroupsMutation = useMutation({
    mutationFn: ({ userId, analysisGroups }: { userId: string; analysisGroups: string[] | null }) =>
      updateUserAnalysisGroupsApi(userId, analysisGroups),
    onMutate: async ({ userId, analysisGroups }) => {
      // Cancel outgoing refetches (so they don't overwrite our optimistic update)
      await queryClient.cancelQueries({ queryKey: userKeys.list(tenantId!) });

      // Snapshot the previous value
      const previousUsers = queryClient.getQueryData<AccountUser[]>(userKeys.list(tenantId!));

      // Optimistically update to the new value
      if (previousUsers) {
        const updatedUsers = previousUsers.map(user =>
          user.id === userId
            ? { ...user, analysisGroups: analysisGroups || undefined }
            : user
        );
        queryClient.setQueryData(userKeys.list(tenantId!), updatedUsers);
      }

      // Return a context object with the snapshot and variables
      return { previousUsers, userId, analysisGroups };
    },
    onError: (error, _variables, context) => {
      // If the mutation fails, use the context returned from onMutate to roll back
      if (context?.previousUsers) {
        queryClient.setQueryData(userKeys.list(tenantId!), context.previousUsers);
      }
      logError("Error updating user analysis groups", error);
    },
    onSettled: () => {
      // Always refetch after error or success to ensure data consistency
      queryClient.invalidateQueries({ queryKey: userKeys.list(tenantId!) });
    }
  });

  return {
    users: usersQuery.data || [],
    isLoading: usersQuery.isLoading,
    error: usersQuery.error as Error | null,
    inviteUser: {
      mutate: inviteUserMutation.mutate,
      isPending: inviteUserMutation.isPending,
      error: inviteUserMutation.error as Error | null,
      isSuccess: inviteUserMutation.isSuccess
    },
    deleteUser: {
      mutate: deleteUserMutation.mutate,
      isPending: deleteUserMutation.isPending,
      error: deleteUserMutation.error as Error | null,
      isSuccess: deleteUserMutation.isSuccess
    },
    updateUserAnalysisGroups: {
      mutate: updateUserAnalysisGroupsMutation.mutate,
      isPending: updateUserAnalysisGroupsMutation.isPending,
      error: updateUserAnalysisGroupsMutation.error as Error | null,
      isSuccess: updateUserAnalysisGroupsMutation.isSuccess
    },
    refreshUsers: () => queryClient.invalidateQueries({ queryKey: userKeys.list(tenantId!) })
  };
};
